import { useMutation } from '@apollo/client';
import {
  MemberFilter,
  MemberSort,
  QueryMembersForBulletinQuery,
  QueryMembersForBulletinQueryVariables,
} from '__generated__/graphql';
import { MEMBER_UPDATE_MUTATION } from 'apollo/mutations/member-mutations';
import { MEMBERS_FOR_BULLETIN_QUERY } from 'containers/bulletin/hooks/useMembersLazyLoading/useMembersLazyLoading';
import { employeesQuery } from 'containers/manage-employees/employees-queries';
import { useApolloPaging } from 'hooks';
import useGraphQLPaging from 'hooks/graphql/useGraphQLPaging/useGraphQLPaging';
import _ from 'lodash';
import { IMember, IMemberUpdateInput } from 'types';
import ICursorable from 'types/Cursorable';

export default function useMember() {
  const { getAll } = useApolloPaging();
  const [updateMemberMutation] = useMutation(MEMBER_UPDATE_MUTATION);
  const pagingClient = useGraphQLPaging();

  async function getMembers(groupId: string, query: any = employeesQuery) {
    const results = await getAll<IMember & ICursorable>('members', {
      query,
      variables: {
        first: 500,
        filter: {
          memberGroupId: { equal: groupId },
          archivedOn: { isNull: true },
        },
      },
      fetchPolicy: 'network-only',
    });
    return results;
  }

  async function getAllMembers(filterInput?: MemberFilter, filterSort?: MemberSort) {
    const result = await pagingClient<QueryMembersForBulletinQuery, QueryMembersForBulletinQueryVariables>(
      {
        document: MEMBERS_FOR_BULLETIN_QUERY,
        variables: {
          first: 200,
          filter: filterInput,
          sort: filterSort,
        },
      },
      'members'
    );

    return result;
  }

  function updateInputs(inputs: IMemberUpdateInput[]) {
    return inputs.map(async (member) => {
      const result = await updateMemberMutation({ variables: { member } });
      const newMember = result.data?.updateMember;

      if (newMember) {
        return newMember;
      } else {
        return null;
      }
    });
  }

  function handlePromises(
    promises: Array<Promise<any>>,
    resolve: (value: any[]) => void,
    reject: (value: any[]) => void
  ) {
    return Promise.all(promises).then((resolved) => {
      const [failed, successful] = _.partition(resolved, _.isNull);
      if (failed.length === 0) {
        resolve(successful);
      } else {
        reject(failed);
      }
    });
  }

  function update(inputs: IMemberUpdateInput[]) {
    return new Promise((resolve, reject) => {
      const promises = updateInputs(inputs);
      return handlePromises(promises, resolve, reject);
    });
  }

  async function assignMembersToGroup(groupId: string, memberIds: string[]) {
    const inputs: IMemberUpdateInput[] = memberIds.map((id) => {
      const item: IMemberUpdateInput = {
        id,
        memberGroupId: groupId,
      };
      return item;
    });

    return update(inputs);
  }

  async function unassignMembersFromGroup(memberIds: string[]) {
    const inputs: IMemberUpdateInput[] = memberIds.map((id) => {
      const item: IMemberUpdateInput = {
        id,
        memberGroupId: null,
      };
      return item;
    });

    return update(inputs);
  }

  async function unassignAllMembersFromGroup(groupId: string) {
    const members = await getMembers(groupId);
    return unassignMembersFromGroup(members.flatMap((item) => item.id));
  }

  async function updateMemberPositions(positionId: string, memberIds: string[]) {
    const inputs: IMemberUpdateInput[] = memberIds.map((id) => {
      const item: IMemberUpdateInput = {
        id,
        positionId,
      };
      return item;
    });

    return update(inputs);
  }

  return {
    getMembers,
    getAllMembers,
    assignMembersToGroup,
    unassignMembersFromGroup,
    unassignAllMembersFromGroup,
    updateMemberPositions,
  };
}
